import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { admin } from './admin-api';

export default function AdminHome() {
  const sys = useQuery({ queryKey: ['admin', 'system'], queryFn: admin.system });
  const integ = useQuery({ queryKey: ['admin', 'integrations'], queryFn: admin.integrations });
  const users = useQuery({ queryKey: ['admin', 'users'], queryFn: admin.users });

  return (
    <div>
      <div className="ph">Admin · Overview</div>
      <div className="h2">Platform status</div>
      <p className="lead">Operator-only view. Health, integration state, and demo controls live here.</p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 14 }}>
        <Stat label="Uptime" value={sys.data ? `${Math.floor(sys.data.uptime_s / 60)}m` : '—'} color="teal" />
        <Stat label="Memory" value={sys.data ? `${sys.data.memory_mb.toFixed(0)} MB` : '—'} color="amber" />
        <Stat label="Users" value={users.data ? `${users.data.length}` : '—'} color="blue" />
        <Stat
          label="Gemini"
          value={integ.data ? (integ.data.gemini.configured ? 'connected' : 'unconfigured') : '—'}
          color={integ.data?.gemini.configured ? 'olive' : 'muted'}
        />
      </div>

      <div className="grid2">
        <div className="panel">
          <div className="qhead"><span className="qdot" style={{ background: 'var(--teal)' }} />Integrations</div>
          {integ.data ? (
            <>
              <Row k="Gemini AI" v={integ.data.gemini.configured ? `connected · ${integ.data.gemini.model}` : 'not configured'} />
              <Row k="SAP" v={`${integ.data.sap.connection_status} · ${integ.data.sap.sku_count} SKUs`} />
              <div style={{ marginTop: 10 }}>
                <Link to="/admin/integrations" className="btn ghost">Manage →</Link>
              </div>
            </>
          ) : (
            <div className="small faint">Loading integrations…</div>
          )}
        </div>

        <div className="panel">
          <div className="qhead"><span className="qdot" style={{ background: 'var(--gold)' }} />Engine invocations</div>
          {sys.data?.table_row_counts && (
            <>
              {Object.entries(sys.data.table_row_counts).map(([k, v]) => (
                <Row key={k} k={k} v={`${v} rows`} />
              ))}
            </>
          )}
          <div style={{ marginTop: 10 }}>
            <Link to="/admin/engines" className="btn ghost">Engine details →</Link>
          </div>
        </div>
      </div>

      <div className="panel" style={{ marginTop: 14 }}>
        <div className="qhead"><span className="qdot" style={{ background: 'var(--amber)' }} />Quick actions</div>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <Link to="/admin/demo" className="btn primary">Demo controls</Link>
          <Link to="/admin/system" className="btn ghost">System health</Link>
          <Link to="/admin/users" className="btn ghost">Users</Link>
          <Link to="/admin/integrations" className="btn ghost">Integrations</Link>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, color }: { label: string; value: string; color: 'teal' | 'amber' | 'blue' | 'olive' | 'muted' }) {
  return (
    <div className="panel" style={{ padding: 14 }}>
      <div className="small faint" style={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 12 }}>{label}</div>
      <div className="num" style={{ marginTop: 4 }}>{value}</div>
      <span className={`tag ${color}`} style={{ marginTop: 6 }}>{label.toLowerCase()}</span>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="row" style={{ justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #241f18' }}>
      <span style={{ fontSize: 14 }}>{k}</span>
      <span className="mono small">{v}</span>
    </div>
  );
}
